<script setup>
import { computed } from "vue";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";

const props = defineProps({
  step: {
    type: Object,
    required: true,
  },
  isLast: {
    type: Boolean,
    default: false,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["toggle", "open-tab"]);

const isDone = computed(() => props.step.status === "done");
const isCurrent = computed(() => props.step.status === "current");
const isTodo = computed(() => props.step.status === "todo");

const iconName = computed(() => props.step.icon || "sli-main");

const hasDoneData = computed(() => {
  const d = props.step.details;
  return Boolean(d && d.doneData && d.doneData.length);
});

const showDoneData = computed(() => isDone.value && hasDoneData.value);

const hasDetails = computed(() => {
  const d = props.step.details;
  if (!d) return false;
  if (showDoneData.value) return true;
  return Boolean((d.todo && d.todo.length) || d.warning);
});

const hasFooter = computed(() => {
  return Boolean(props.step.details && props.step.details.actionLabel);
});

function onHeaderClick() {
  if (!hasDetails.value) return;
  emit("toggle", props.step);
}

function onOpenTab() {
  emit("open-tab", props.step);
}
</script>

<template>
  <div
    class="stepper-item"
    :class="{
      'stepper-item--done': isDone,
      'stepper-item--current': isCurrent,
      'stepper-item--todo': isTodo,
      'stepper-item--expanded': expanded,
      'stepper-item--has-details': hasDetails,
    }"
  >
    <div class="stepper-item__rail">
      <div class="stepper-item__circle">
        <svg
          v-if="isDone"
          class="stepper-item__check"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <SvgIcon v-else :name="iconName" class="stepper-item__icon" />
      </div>
      <div v-if="!isLast" class="stepper-item__connector">
        <div class="stepper-item__connector-fill" />
      </div>
    </div>

    <div class="stepper-item__content">
      <div
        class="stepper-item__head"
        :class="{ 'stepper-item__head--clickable': hasDetails }"
        @click="onHeaderClick"
      >
        <h4 class="stepper-item__title">{{ step.title }}</h4>
        <SvgIcon
          v-if="hasDetails"
          name="huge-shevron-down"
          class="stepper-item__chevron"
          :class="{ 'stepper-item__chevron--open': expanded }"
        />
      </div>

      <p v-if="!expanded" class="stepper-item__description">
        {{ step.description }}
      </p>

      <transition name="stepper-expand">
        <div v-if="expanded && hasDetails" class="stepper-item__details">
          <div class="stepper-item__body">
            <template v-if="showDoneData">
              <div class="stepper-item__subhead">
                {{ $t("Готово:") }}
              </div>
              <div class="stepper-item__data">
                <div
                  v-for="(row, i) in step.details.doneData"
                  :key="i"
                  class="stepper-item__data-row"
                >
                  <div class="stepper-item__data-label">{{ row.label }}</div>
                  <div class="stepper-item__data-value">{{ row.value }}</div>
                </div>
              </div>
            </template>

            <template v-else>
              <div
                v-if="step.details.todo && step.details.todo.length"
                class="stepper-item__subhead"
              >
                {{ $t("Что нужно сделать:") }}
              </div>
              <ul
                v-if="step.details.todo && step.details.todo.length"
                class="stepper-item__list"
              >
                <li
                  v-for="(item, i) in step.details.todo"
                  :key="i"
                  class="stepper-item__list-item"
                >
                  {{ item }}
                </li>
              </ul>
            </template>

            <div v-if="step.details.warning" class="stepper-item__warning">
              <SvgIcon
                name="sli-info"
                class="stepper-item__warning-icon"
                style="fill: none"
              />
              <div class="stepper-item__warning-text">
                {{ step.details.warning }}
              </div>
            </div>
          </div>

          <button
            v-if="hasFooter"
            type="button"
            class="stepper-item__footer"
            :class="{ 'stepper-item__footer--current': step.isCurrentTab }"
            :disabled="step.isCurrentTab"
            @click.stop="onOpenTab"
          >
            <SvgIcon
              v-if="step.isCurrentTab"
              name="sli-selected"
              class="stepper-item__footer-icon stepper-item__footer-icon--left"
            />
            <div class="stepper-item__footer-text">
              {{
                step.isCurrentTab
                  ? $t("Вы на этой вкладке")
                  : step.details.actionLabel
              }}
            </div>
            <SvgIcon
              v-if="!step.isCurrentTab"
              name="sli-right"
              class="stepper-item__footer-icon"
            />
          </button>
        </div>
      </transition>
    </div>
  </div>
</template>

<style scoped>
.stepper-item {
  display: flex;
  gap: 18px;
  align-items: stretch;
  position: relative;
  transition: opacity 200ms ease;
}

.stepper-item--todo {
  opacity: 0.75;
}

.stepper-item__rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 28px;
}

.stepper-item__circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: hsl(var(--card));
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px hsl(var(--foreground) / 0.05),
    0 0 0 1px hsl(var(--border));
  flex-shrink: 0;
  transition: background 320ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 320ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
  z-index: 1;
}

.stepper-item--done .stepper-item__circle {
  background: linear-gradient(135deg, #0f172a 0%, #334155 100%);
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.22);
}

.stepper-item--current .stepper-item__circle {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.12),
    0 2px 8px rgba(99, 102, 241, 0.22);
  animation: stepper-glow 3s ease-in-out infinite;
}

@keyframes stepper-glow {
  0%,
  100% {
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1),
      0 2px 8px rgba(99, 102, 241, 0.2);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(99, 102, 241, 0.06),
      0 2px 12px rgba(99, 102, 241, 0.28);
  }
}

@media (prefers-reduced-motion: reduce) {
  .stepper-item--current .stepper-item__circle {
    animation: none;
  }
}

.stepper-item__icon {
  width: 13px;
  height: 13px;
  color: hsl(var(--muted-foreground));
  transition: color 200ms ease;
}

.stepper-item--current .stepper-item__icon {
  color: #ffffff;
}

.stepper-item__check {
  width: 14px;
  height: 14px;
  fill: none;
  stroke: #ffffff;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.stepper-item__connector {
  flex: 1;
  width: 0;
  margin-top: 6px;
  margin-bottom: 6px;
  border-left: 1.5px dashed hsl(var(--border));
  min-height: 32px;
  transition: border-left-color 200ms ease;
}

.stepper-item--done .stepper-item__connector {
  border-left-color: #f9a8d4;
}

.stepper-item__connector-fill {
  display: none;
}

.stepper-item__content {
  flex: 1;
  padding-top: 4px;
  padding-bottom: 26px;
  min-width: 0;
}

.stepper-item--expanded .stepper-item__content {
  padding-bottom: 16px;
}

.stepper-item__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 3px;
}

.stepper-item__head--clickable {
  cursor: pointer;
  user-select: none;
}

.stepper-item__head--clickable:hover .stepper-item__title {
  color: hsl(var(--primary));
}

.stepper-item__title {
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  color: hsl(var(--foreground));
  margin: 0;
  transition: color 0.2s ease;
  flex: 1;
  min-width: 0;
}

.stepper-item--todo .stepper-item__title {
  color: hsl(var(--muted-foreground));
}

.stepper-item__chevron {
  width: 12px;
  height: 12px;
  color: hsl(var(--muted-foreground));
  flex-shrink: 0;
  transition: transform 0.25s ease, color 0.2s ease;
}

.stepper-item__chevron--open {
  transform: rotate(180deg);
  color: #6366f1;
}

.stepper-item__description {
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
  color: hsl(var(--muted-foreground));
  margin: 0;
}

.stepper-item__summary {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 6px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.2);
  font-size: 11px;
  font-weight: 500;
  color: #047857;
  line-height: 1.45;
  max-width: 100%;
}

.stepper-item__summary-text {
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.stepper-item__summary-icon {
  width: 12px;
  height: 12px;
  color: #10b981;
  flex-shrink: 0;
  margin-top: 2px;
}

.stepper-item__details {
  margin-top: 8px;
  border: 1px solid hsl(var(--border));
  background: hsl(var(--card));
  box-shadow: 0 2px 6px hsl(var(--foreground) / 0.04);
  overflow: hidden;
  border-radius: var(--radius);
}

.stepper-item__body {
  padding: 12px 14px;
}

.stepper-item__subhead {
  font-size: 11px;
  font-weight: 600;
  color: hsl(var(--foreground));
  margin-bottom: 8px;
  letter-spacing: 0.01em;
}

.stepper-item__list {
  list-style: none;
  padding: 0;
  margin: 0 0 10px 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.stepper-item__list-item {
  position: relative;
  padding-left: 14px;
  font-size: 12px;
  line-height: 1.45;
  color: hsl(var(--muted-foreground));
}

.stepper-item__list-item::before {
  content: "";
  position: absolute;
  left: 4px;
  top: 7px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: hsl(var(--muted-foreground));
}

.stepper-item__data {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
}

.stepper-item__data-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stepper-item__data-label {
  font-size: 10px;
  font-weight: 500;
  color: hsl(var(--muted-foreground));
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.stepper-item__data-value {
  font-size: 12px;
  font-weight: 600;
  color: hsl(var(--foreground));
  word-break: break-word;
  line-height: 1.4;
}

.stepper-item__warning {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 4px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.18);
  font-size: 11px;
  line-height: 1.45;
  color: #92400e;
}

.stepper-item__warning-icon {
  width: 12px;
  height: 12px;
  color: #d97706;
  flex-shrink: 0;
  margin-top: 2px;
}

.stepper-item__footer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  border: none;
  border-top: 1px solid hsl(var(--border));
  background: rgba(99, 102, 241, 0.04);
  font-size: 12px;
  font-weight: 600;
  color: #6366f1;
  cursor: pointer;
  transition: all 0.15s ease;
}

.stepper-item__footer:hover {
  background: rgba(99, 102, 241, 0.08);
}

.stepper-item__footer-icon {
  width: 12px;
  height: 12px;
  color: #6366f1;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.stepper-item__footer:hover .stepper-item__footer-icon {
  transform: translateX(2px);
}

.stepper-item__footer--current {
  background: rgba(16, 185, 129, 0.08);
  color: #047857;
  cursor: default;
  justify-content: flex-start;
  gap: 6px;
}

.stepper-item__footer--current:hover {
  background: rgba(16, 185, 129, 0.08);
}

.stepper-item__footer--current .stepper-item__footer-icon {
  color: #10b981;
}

.stepper-item__footer-icon--left {
  margin-right: 2px;
}

.stepper-expand-enter-active {
  transition: max-height 320ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 240ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 320ms cubic-bezier(0.22, 1, 0.36, 1),
    margin 320ms cubic-bezier(0.22, 1, 0.36, 1);
  overflow: hidden;
}

.stepper-expand-leave-active {
  transition: max-height 0.22s cubic-bezier(0.4, 0, 0.6, 1), opacity 0.18s ease,
    transform 0.22s ease, margin 0.22s ease;
  overflow: hidden;
}

.stepper-expand-enter-from,
.stepper-expand-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-4px);
  margin-top: 0 !important;
}

.stepper-expand-enter-to,
.stepper-expand-leave-from {
  max-height: 600px;
  opacity: 1;
  transform: translateY(0);
}
</style>
